import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	GameColorPalette,
	ColorPurpose,
	GameCustomizationService,
} from '@meta-press/shared-plugins';

/**
 * Type definition for which colors to display in the preview
 * Now uses ColorPurpose enum values
 */
export type PreviewColorType = ColorPurpose;

/**
 * Component for displaying a small preview of a color palette
 * Shows the main colors in a compact visual format
 * Can display light and/or dark themes with customizable color selection
 */
@Component({
	selector: 'app-color-palette-preview',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './color-palette-preview.component.html',
	styleUrls: ['./color-palette-preview.component.scss'],
})
export class ColorPalettePreviewComponent {
	// Inject GameCustomizationService for color purpose definitions
	private gameCustomizationService = inject(GameCustomizationService);

	@Input() palette?: GameColorPalette;
	@Input() size: 'small' | 'medium' | 'full-width' = 'small';
	@Input() showName = false;

	/**
	 * Display mode for the preview
	 * 'detailed' = vertical layout (for modals)
	 * 'compact' = horizontal layout with colors in a row (for lists)
	 */
	@Input() displayMode: 'detailed' | 'compact' = 'detailed';

	/**
	 * Which colors to show in the preview
	 * Can be an array of specific ColorPurpose values or 'all' to show all colors
	 * Default: 'all' (shows all colors in the palette)
	 */
	@Input() colorsToShow: PreviewColorType[] | 'all' = 'all';

	/**
	 * Optional filter to show only specific ColorPurpose values
	 * If provided, only colors matching these purposes will be displayed
	 * If not provided, all colors (or colors from colorsToShow) will be shown
	 */
	@Input() visibleColors?: ColorPurpose[];

	/**
	 * Whether to show color labels on the color samples
	 * Default: false
	 */
	@Input() showColorLabels = false;

	/**
	 * Get display colors based on colorsToShow and visibleColors inputs
	 * Extracts color values from palette.colors Map
	 * If visibleColors is provided, only shows those purposes
	 */
	getColors(): { color: string; label: string }[] {
		if (!this.palette?.colors) {
			return [
				{ color: '#ffdc00', label: 'Pääväri' },
				{ color: '#ffc107', label: 'Toissijainen väri' },
				{ color: '#ffffff', label: 'Taustaväri' },
			];
		}

		let colors: { color: string; label: string; purpose: ColorPurpose }[] = [];

		// First, get colors based on colorsToShow
		if (this.colorsToShow === 'all') {
			// Get all colors with their purposes
			for (const [purpose, colorObj] of this.palette.colors.entries()) {
				const definition =
					this.gameCustomizationService.getColorPurposeDefinition(purpose);
				const label = definition?.defaultName || purpose.toString();
				colors.push({ color: colorObj.color, label, purpose });
			}
		} else {
			// Get only specified color purposes
			for (const purpose of this.colorsToShow) {
				const colorObj = this.palette.colors.get(purpose);
				if (colorObj) {
					const definition =
						this.gameCustomizationService.getColorPurposeDefinition(purpose);
					const label = definition?.defaultName || purpose.toString();
					colors.push({ color: colorObj.color, label, purpose });
				}
			}
		}

		// Apply visibleColors filter if provided
		if (this.visibleColors && this.visibleColors.length > 0) {
			colors = colors.filter(c => this.visibleColors!.includes(c.purpose));
		}

		// Return without purpose property (for backward compatibility)
		return colors.map(({ color, label }) => ({ color, label }));
	}

	/**
	 * Get all available colors (used when colorsToShow === 'all')
	 * Iterates through the entire palette.colors Map
	 */
	private getAllColors(): { color: string; label: string }[] {
		if (!this.palette?.colors) {
			return [];
		}

		const colors: { color: string; label: string }[] = [];

		// Iterate through all colors in the palette's Map
		for (const [purpose, colorObj] of this.palette.colors.entries()) {
			const definition =
				this.gameCustomizationService.getColorPurposeDefinition(purpose);
			const label = definition?.defaultName || purpose.toString();
			colors.push({ color: colorObj.color, label });
		}

		return colors;
	}

	/**
	 * Get display colors for the preview (backwards compatibility)
	 * Returns only color values without labels
	 */
	getDisplayColors(): string[] {
		return this.getColors().map(c => c.color);
	}

	/**
	 * TrackBy function for color list performance
	 */
	trackByColor(_index: number, color: string): string {
		return color;
	}
}
